import { useState } from 'react';
import { IconPackage } from './Icons';
import { mediaUrl, mediaSrcSet } from './config';

// Drop-in <img> replacement for product photos: shows a shimmer placeholder
// until the image finishes loading, and falls back to a neutral icon if the
// image fails to load, instead of a broken-image glyph. Renders as a sibling
// pair (shimmer + img), so it slots into an existing `position: relative`
// wrapper without changing that wrapper's layout or its other absolutely
// positioned children (badges, buttons, etc).
//
// `sizes` describes how wide this image will actually be drawn, so the browser
// can pick the right file from the srcSet BEFORE layout -- it is evaluated
// against the viewport, not the element. Getting it wrong only costs bytes,
// never correctness, but the default below assumes the storefront grid: full
// width on a phone, roughly a third on tablet, roughly a quarter on desktop.
// Pass an explicit value anywhere the image is drawn much smaller (a cart
// line, a compare cell) or much larger (a detail-page hero).
export default function ProductImage({
  src,
  alt,
  className,
  style,
  loading = 'lazy',
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 25vw',
  // `fetchPriority="high"` on the first image a shopper sees tells the browser
  // not to queue it behind lazy ones below the fold. Only worth setting on the
  // one or two images above the fold -- marking everything high priority is
  // the same as marking nothing.
  //
  // Passed through as camelCase below. React 19 knows this DOM property and
  // warns ("Invalid DOM property `fetchpriority`") on the all-lowercase
  // spelling, which it then does not apply -- so the hint this prop exists to
  // give was never reaching the browser at all.
  fetchPriority,
}) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  return (
    <>
      {status !== 'loaded' && (
        <div
          className="skeleton pimg-placeholder"
          style={{ position: 'absolute', inset: 0, display: status === 'error' ? 'flex' : 'block', alignItems: 'center', justifyContent: 'center' }}
        >
          {status === 'error' && <IconPackage size={28} style={{ color: 'var(--text-tertiary)' }} />}
        </div>
      )}
      {status !== 'error' && (
        <img
          src={mediaUrl(src)}
          // Undefined for anything not served from our own /uploads -- remote
          // seller URLs, data:/blob: previews in the product editor -- which
          // makes the browser fall back to `src` alone. See mediaSrcSet.
          srcSet={mediaSrcSet(src)}
          sizes={mediaSrcSet(src) ? sizes : undefined}
          alt={alt}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          className={className}
          style={{ ...style, opacity: status === 'loaded' ? 1 : 0 }}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      )}
    </>
  );
}
